"use client";

import { useMemo, useState } from "react";
import { RotateCcw } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { Label } from "@/shadcn-components/ui/label";
import { Switch } from "@/shadcn-components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn-components/ui/select";
import { Textarea } from "@/shadcn-components/ui/textarea";
import ConfigSection from "@/components/chart/chart-indicators/indicators/common/ConfigSection";
import {
  getDefaultSettings,
  getDrawingDefinition,
} from "@/lib/chart-drawings/registry";
import type {
  DrawingInstance,
  DrawingPoint,
  LineStyle,
} from "@/lib/chart-drawings/types";
import { DEFAULT_DRAWING_COLOR } from "@/lib/chart-drawings/defaults";
import DrawingStyleEditor from "./DrawingStyleEditor";

/** Maps a STYLE_PRESETS preset id to the underlying setting keys it edits. */
type StyleFieldKind = "line" | "fill" | "text" | "background";

interface StyleFieldMapping {
  kind: StyleFieldKind;
  colorKey: string;
  styleKey?: string;
  widthKey?: string;
  sizeKey?: string;
}

const STYLE_FIELD_MAP: Record<string, StyleFieldMapping> = {
  line: {
    kind: "line",
    colorKey: "color",
    styleKey: "lineStyle",
    widthKey: "lineWidth",
  },
  channel: {
    kind: "line",
    colorKey: "color",
    styleKey: "lineStyle",
    widthKey: "lineWidth",
  },
  grid: {
    kind: "line",
    colorKey: "color",
    styleKey: "lineStyle",
    widthKey: "lineWidth",
  },
  wave: {
    kind: "line",
    colorKey: "color",
    styleKey: "lineStyle",
    widthKey: "lineWidth",
  },
  border: {
    kind: "line",
    colorKey: "borderColor",
    styleKey: "lineStyle",
    widthKey: "lineWidth",
  },
  lines: {
    kind: "line",
    colorKey: "lineColor",
    styleKey: "lineStyle",
    widthKey: "lineWidth",
  },
  fill: { kind: "fill", colorKey: "fillColor" },
  text: { kind: "text", colorKey: "textColor", sizeKey: "fontSize" },
  background: { kind: "background", colorKey: "backgroundColor" },
};

/**
 * Drawings rendered as a filled label chip. For these, one colour drives fill,
 * border and (via auto-ink) the caption — splitting them across three separate
 * swatches is what let a drawing end up with a recoloured border on a stale
 * blue body. The split stays reachable behind "Advanced".
 */
const CHIP_DRAWING_TYPES = new Set(["text-label", "price-label"]);

/** Keys written together when the single Colour control changes. */
const CHIP_COLOR_KEYS = ["color", "backgroundColor", "borderColor"] as const;

const FIB_TOGGLES: Array<{ key: string; label: string; hint: string; defaultOn: boolean }> = [
  {
    key: "showPrices",
    label: "Show prices",
    hint: "Print the price each ratio sits on",
    defaultOn: true,
  },
  {
    key: "showBands",
    label: "Show bands",
    hint: "Shade the zones between levels",
    defaultOn: true,
  },
  {
    key: "anchorEmphasis",
    label: "Emphasize anchors",
    hint: "Draw 0 / 50 / 100% heavier",
    defaultOn: true,
  },
  {
    key: "extendRight",
    label: "Extend right",
    hint: "Project levels past the last anchor",
    defaultOn: false,
  },
];

interface BaseDrawingConfigProps {
  drawing: DrawingInstance;
  onBack: () => void;
  onSave: (updated: DrawingInstance) => void;
}

export default function BaseDrawingConfig({
  drawing,
  onBack,
  onSave,
}: BaseDrawingConfigProps) {
  const def = getDrawingDefinition(drawing.type);

  const [name, setName] = useState(drawing.name);
  const [textContent, setTextContent] = useState(
    (drawing.settings?.text as string | undefined) ?? ""
  );
  const [points, setPoints] = useState<DrawingPoint[]>(() =>
    (drawing.settings?.points ?? []).map((p) => ({ ...p }))
  );
  const [style, setStyle] = useState<Record<string, unknown>>(() => ({
    ...(drawing.settings?.style ?? {}),
  }));
  const [values, setValues] = useState<
    Record<string, number | string | boolean>
  >(() => ({ ...(drawing.settings?.values ?? {}) }));
  const [drawAsBackground, setDrawAsBackground] = useState(
    Boolean(drawing.settings?.drawAsBackground)
  );

  const showDrawAsBackground = useMemo(
    () =>
      drawing.type === "horizontal-line" ||
      drawing.type === "vertical-line" ||
      drawing.type === "trendline" ||
      drawing.type === "rectangle",
    [drawing.type]
  );

  const requiresText =
    def?.requiresTextPrompt === true ||
    drawing.type === "text" ||
    drawing.type === "text-label";
  const isPriceLabel = drawing.type === "price-label";

  const isChipDrawing = CHIP_DRAWING_TYPES.has(drawing.type);
  const isFibonacci = drawing.type.startsWith("fibonacci-");
  const [advancedColors, setAdvancedColors] = useState(false);

  const chipColor =
    (style.backgroundColor as string | undefined) ??
    (style.color as string | undefined) ??
    (style.borderColor as string | undefined) ??
    DEFAULT_DRAWING_COLOR;

  const setChipColor = (c: string) =>
    setStyle((prev) => {
      const next = { ...prev };
      CHIP_COLOR_KEYS.forEach((k) => {
        next[k] = c;
      });
      // Drop any pinned caption colour so the chip re-inks for contrast.
      delete next.textColor;
      return next;
    });

  const handleReset = () => {
    const defaults = getDefaultSettings(drawing.type);
    setStyle({ ...(defaults.style ?? {}) });
    setValues({ ...(defaults.values ?? {}) });
    setDrawAsBackground(false);
  };

  const handleSave = () => {
    const cleanedPoints = points.length
      ? points.map((p) => ({ ...p }))
      : drawing.settings?.points;

    const updated: DrawingInstance = {
      ...drawing,
      name: name.trim() || drawing.name,
      settings: {
        ...drawing.settings,
        ...(cleanedPoints ? { points: cleanedPoints } : {}),
        style,
        ...(Object.keys(values).length ? { values } : {}),
        ...(requiresText || isPriceLabel ? { text: textContent } : {}),
        ...(showDrawAsBackground ? { drawAsBackground } : {}),
      },
    };
    onSave(updated);
    onBack();
  };

  const updatePoint = (index: number, patch: Partial<DrawingPoint>) => {
    setPoints((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  return (
    <div className="flex flex-col min-h-0 flex-1 bg-background">
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        <ConfigSection
          title="General"
          description="Display name shown in the active list"
        >
          <div className="flex flex-col gap-1.5 py-2.5">
            <Label className="text-sm font-normal text-foreground">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-9 text-sm"
              placeholder={drawing.name}
            />
          </div>
        </ConfigSection>

        {requiresText || isPriceLabel ? (
          <ConfigSection
            title="Content"
            description={
              isPriceLabel
                ? "Text rendered inside the price tag"
                : "Text rendered on the chart"
            }
          >
            <div className="flex flex-col gap-1.5 py-2.5">
              <Label className="text-sm font-normal text-foreground">
                Text
              </Label>
              <Textarea
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                rows={3}
                className="text-sm resize-none"
                placeholder="Enter text…"
              />
            </div>
          </ConfigSection>
        ) : null}

        {points.length > 0 ? (
          <ConfigSection
            title="Coordinates"
            description="Anchor points on the chart"
          >
            {points.map((point, idx) => (
              <PointRow
                key={idx}
                index={idx}
                point={point}
                drawingType={drawing.type}
                onChange={(patch) => updatePoint(idx, patch)}
              />
            ))}
          </ConfigSection>
        ) : null}

        {def && def.parameters.length > 0 ? (
          <ConfigSection
            title="Parameters"
            description="Behavior of this drawing"
          >
            {def.parameters.map((param) => (
              <ParameterRow
                key={param.name}
                param={param}
                value={values[param.name] ?? param.defaultValue}
                onChange={(v) =>
                  setValues((prev) => ({ ...prev, [param.name]: v }))
                }
              />
            ))}
          </ConfigSection>
        ) : null}

        {def && def.styles.length > 0 ? (
          <ConfigSection
            title="Appearance"
            description={
              isChipDrawing
                ? "One color fills the label; the caption auto-contrasts"
                : "Colors and line styles on the chart"
            }
          >
            {isChipDrawing ? (
              <>
                <DrawingStyleEditor
                  label="Color"
                  color={chipColor}
                  onColorChange={setChipColor}
                  fontSize={(style.fontSize as number | undefined) ?? 11}
                  onFontSizeChange={(sz) =>
                    setStyle((prev) => ({ ...prev, fontSize: sz }))
                  }
                />
                <div className="flex items-center justify-between py-2.5">
                  <div className="flex flex-col">
                    <Label className="text-sm font-normal text-foreground">
                      Advanced
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Set fill, border and text separately
                    </p>
                  </div>
                  <Switch
                    checked={advancedColors}
                    onCheckedChange={setAdvancedColors}
                  />
                </div>
              </>
            ) : null}

            {isChipDrawing && !advancedColors
              ? null
              : def.styles.map((preset) => {
              const mapping = STYLE_FIELD_MAP[preset.id];
              if (!mapping) return null;

              const color =
                (style[mapping.colorKey] as string | undefined) ??
                preset.defaultColor;
              const lineStyle = mapping.styleKey
                ? ((style[mapping.styleKey] as LineStyle | undefined) ??
                  preset.defaultStyle)
                : undefined;
              const lineWidth = mapping.widthKey
                ? ((style[mapping.widthKey] as number | undefined) ?? 1)
                : undefined;
              const fontSize = mapping.sizeKey
                ? ((style[mapping.sizeKey] as number | undefined) ?? 12)
                : undefined;

              return (
                <DrawingStyleEditor
                  key={preset.id}
                  label={preset.label}
                  color={color}
                  onColorChange={(c) =>
                    setStyle((prev) => ({ ...prev, [mapping.colorKey]: c }))
                  }
                  lineStyle={lineStyle}
                  onLineStyleChange={
                    mapping.styleKey
                      ? (s) =>
                          setStyle((prev) => ({
                            ...prev,
                            [mapping.styleKey!]: s,
                          }))
                      : undefined
                  }
                  lineWidth={lineWidth}
                  onLineWidthChange={
                    mapping.widthKey
                      ? (w) =>
                          setStyle((prev) => ({
                            ...prev,
                            [mapping.widthKey!]: w,
                          }))
                      : undefined
                  }
                  fontSize={fontSize}
                  onFontSizeChange={
                    mapping.sizeKey
                      ? (sz) =>
                          setStyle((prev) => ({
                            ...prev,
                            [mapping.sizeKey!]: sz,
                          }))
                      : undefined
                  }
                />
              );
              })}
          </ConfigSection>
        ) : null}

        {isFibonacci ? (
          <ConfigSection
            title="Fibonacci"
            description="Levels are clipped to this drawing's start and end time"
          >
            {FIB_TOGGLES.map((t) => (
              <div
                key={t.key}
                className="flex items-center justify-between py-2.5"
              >
                <div className="flex flex-col">
                  <Label className="text-sm font-normal text-foreground">
                    {t.label}
                  </Label>
                  <p className="text-xs text-muted-foreground">{t.hint}</p>
                </div>
                <Switch
                  checked={(style[t.key] as boolean | undefined) ?? t.defaultOn}
                  onCheckedChange={(v) =>
                    setStyle((prev) => ({ ...prev, [t.key]: v }))
                  }
                />
              </div>
            ))}
          </ConfigSection>
        ) : null}

        {showDrawAsBackground ? (
          <ConfigSection title="Display">
            <div className="flex items-center justify-between py-2.5">
              <div className="flex flex-col">
                <Label className="text-sm font-normal text-foreground">
                  Draw as background
                </Label>
                <p className="text-xs text-muted-foreground">
                  Render behind candles
                </p>
              </div>
              <Switch
                checked={drawAsBackground}
                onCheckedChange={setDrawAsBackground}
              />
            </div>
          </ConfigSection>
        ) : null}
      </div>

      <div className="shrink-0 border-t border-border bg-background/95 backdrop-blur-sm px-3 py-3">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1 gap-1.5"
            onClick={handleReset}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </Button>
          <Button type="button" className="flex-1" onClick={handleSave}>
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}

function PointRow({
  index,
  point,
  drawingType,
  onChange,
}: {
  index: number;
  point: DrawingPoint;
  drawingType: string;
  onChange: (patch: Partial<DrawingPoint>) => void;
}) {
  const showTime = drawingType !== "horizontal-line";
  const showValue = drawingType !== "vertical-line";

  return (
    <div className="py-2.5 border-b border-border/60 last:border-0 space-y-2">
      <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Point {index + 1}
      </Label>
      <div className="grid grid-cols-2 gap-2">
        {showTime ? (
          <div>
            <Label className="text-xs text-muted-foreground">Time</Label>
            <Input
              type="datetime-local"
              value={
                point.timestamp
                  ? new Date(point.timestamp).toISOString().slice(0, 16)
                  : ""
              }
              onChange={(e) => {
                const ts = new Date(e.target.value).getTime();
                if (!Number.isNaN(ts)) onChange({ timestamp: ts });
              }}
              className="h-8 text-xs"
            />
          </div>
        ) : null}
        {showValue ? (
          <div>
            <Label className="text-xs text-muted-foreground">Price</Label>
            <Input
              type="number"
              value={point.value ?? 0}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!Number.isNaN(v)) onChange({ value: v });
              }}
              step="any"
              className="h-8 text-xs tabular-nums"
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ParameterRow({
  param,
  value,
  onChange,
}: {
  param: {
    name: string;
    type: "number" | "color" | "select" | "boolean";
    defaultValue: number | string | boolean;
    options?: string[];
    min?: number;
    max?: number;
    step?: number;
  };
  value: number | string | boolean;
  onChange: (v: number | string | boolean) => void;
}) {
  if (param.type === "boolean") {
    return (
      <div className="flex items-center justify-between py-2.5 border-b border-border/60 last:border-0">
        <Label className="text-sm font-normal text-foreground">
          {param.name}
        </Label>
        <Switch
          checked={Boolean(value)}
          onCheckedChange={(checked) => onChange(checked)}
        />
      </div>
    );
  }

  if (param.type === "number") {
    return (
      <div className="flex items-center justify-between gap-3 py-2.5 border-b border-border/60 last:border-0">
        <Label className="text-sm font-normal text-foreground shrink-0">
          {param.name}
        </Label>
        <Input
          type="number"
          value={Number(value)}
          onChange={(e) => {
            const n = parseFloat(e.target.value);
            if (!Number.isNaN(n)) {
              const clamped = Math.max(
                param.min ?? -Infinity,
                Math.min(param.max ?? Infinity, n)
              );
              onChange(clamped);
            }
          }}
          min={param.min}
          max={param.max}
          step={param.step ?? 1}
          className="h-8 w-24 text-center text-sm tabular-nums"
        />
      </div>
    );
  }

  if (param.type === "select" && param.options) {
    return (
      <div className="flex flex-col gap-1.5 py-2.5 border-b border-border/60 last:border-0">
        <Label className="text-sm font-normal text-foreground">
          {param.name}
        </Label>
        <Select value={String(value)} onValueChange={(v) => onChange(v)}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-56">
            {param.options.map((opt) => (
              <SelectItem key={opt} value={opt} className="text-sm">
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return null;
}
